import React from 'react';
import { ArrowLeft, ArrowRight, Users, Briefcase, Heart, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const profiles = [
  {
    name: 'Annette Brown',
    icon: Heart,
    color: 'amber',
    tagline: 'Classroom Leader Ready to Transition',
    bio: `Annette is entering her 14th year as a Preschool Lead teacher at Shining Light Child Development Center. Annette has a Bachelor's Degree in Early Childhood Education and a Child Development Associate (CDA) credential. She previously held positions as an assistant teacher and teacher aide and has worked with all groups of children birth to five. Annette is a member of NAEYC and is active with the local chapter, DCAEYC. Annette works well in partnership with parents and families and even coordinates family events for her program. Annette has also been asked by her director to represent Shining Light CDC at local conferences. As a Lead Teacher, Annette often provides coaching support and feedback to her assistant teacher and has mentored new staff. Annette has decided that after her 14th year of teaching is complete, she would like to transition out of the classroom into a leadership role either in her current program or with a local agency, where she can continue to coach and support teachers, but also be an advocate and voice for them in rooms where decisions are being made about Early Childhood Education. Annette also wants to sharpen her skills as a supervisor so she can one day lead a team. Annette knows what she wants to do to elevate, but she's not quite sure how to do it.`
  },
  {
    name: 'Amari Jones',
    icon: Building2,
    color: 'orange',
    tagline: 'Educator with Entrepreneurial Vision',
    bio: `Amari is a 5th year early educator at Antioch Baptist Church Child Development Center. As a young adult, Amari assisted his mother with her family child care home business. While he did not provide direct care for the children, he supported his mother with business related tasks such as correspondence, accounting, completing program applications, and organizing files. After earning his CDA as a high school student and going on to earn a Master's Degree in Early Childhood Education, Amari became a preschool teacher. Amari loves his role as an educator but wants to venture out as an entrepreneur. Amari would like to open his own family child care business like his mother. He attended a recent National Family Child Care Association Conference with his mother and is curious about how he can partner with community organizations and really make a change in his neighborhood in Ward 8 by offering high quality early care and education.`
  },
  {
    name: 'Jasmine Carter',
    icon: Users,
    color: 'rose',
    tagline: 'Family Engagement Professional Expanding Influence',
    bio: `Jasmine is the Family Engagement Coordinator at Bright Beginnings Early Learning Center in Ward 7, where she has worked for the past six years. She holds a Bachelor's Degree in Social Work and is currently pursuing a Master's in Public Administration. Jasmine entered early childhood as a parent first. After enrolling her son in the program, she became active in the parent council, then was hired as a parent ambassador, and eventually stepped into her current role coordinating family events, parent education workshops, and community partnerships across the center's three sites. Jasmine is deeply trusted by the families she serves and has built strong partnerships with local nonprofits, healthcare providers, and faith communities in Ward 7. Her director has begun including her in strategic planning conversations, and she recently led the program's response to a community survey identifying gaps in mental health support for families. Jasmine is interested in moving into a Director of Family Services or Community Partnerships role, but she wants to sharpen her skills in budget management, team supervision, and systems-level thinking. She also wants to better understand how to influence policy at the city level so the lived experience of the families she serves shapes decisions in rooms she has not yet entered.`
  },
  {
    name: 'Marcus Thompson',
    icon: Briefcase,
    color: 'violet',
    tagline: 'Operations Leader Ready to Shape Strategy',
    bio: `Marcus is the Operations Manager at Little Scholars Academy, a network of three early learning centers serving more than 200 children across Wards 4, 5, and 8. He has worked in early childhood operations for eight years after transitioning from a career in nonprofit finance. Marcus holds a Bachelor's Degree in Business Administration and oversees facilities, vendor contracts, technology systems, compliance with licensing requirements, and back-office support for the center directors. Marcus is the person every director calls when something breaks, whether literally or systemically. He has built strong relationships with city agencies, contractors, and vendors and has streamlined operations to free up directors to focus on instructional quality. Marcus is ready to step into an Executive Director or Chief Operating Officer role within a larger early childhood organization, but he recognizes that operations alone will not get him there. He wants to deepen his understanding of program quality, workforce development, and equity-centered leadership so he can lead beyond the back office and contribute to the strategic direction of the organizations he serves.`
  }
];

const colorMap = {
  amber: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', accent: 'bg-amber-500' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', accent: 'bg-orange-500' },
  rose: { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200', accent: 'bg-rose-500' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-200', accent: 'bg-violet-500' }
};

const IdealCandidate = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section id="main-content" role="main" tabIndex={-1} className="relative pt-20 pb-16 bg-gradient-to-br from-amber-600 via-orange-600 to-rose-700">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-300/10 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
          <Link to="/programs/mselp" className="inline-flex items-center gap-2 text-amber-200 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Emerging Leaders Program
          </Link>
          <div className="max-w-4xl">
            <span className="inline-block px-4 py-2 bg-white/20 text-white rounded-full text-sm font-semibold mb-6">
              Maurice Sykes Emerging Leaders Program
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="ideal-candidate-title">
              Who is the Ideal Candidate?
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-slate-700 leading-relaxed">
            The Maurice Sykes Emerging Leaders Program serves emerging leaders across the full early childhood ecosystem, including educators preparing to step out of the classroom, family engagement professionals expanding their influence, operations leaders ready to shape strategy, and others on the path to formal leadership. The profiles below illustrate the range of candidates the program is designed to support.
          </p>
        </div>
      </section>

      {/* Profiles */}
      <section className="pb-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {profiles.map((profile, i) => {
            const Icon = profile.icon;
            const colors = colorMap[profile.color];
            return (
              <div key={i} className={`rounded-2xl border ${colors.border} overflow-hidden shadow-lg`}>
                <div className={`flex items-center gap-4 px-6 py-4 ${colors.bg}`}>
                  <div className={`w-12 h-12 ${colors.accent} rounded-xl flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Emerging Leader Profile #{i + 1}</p>
                    <h3 className="text-xl font-bold text-slate-800">{profile.name}</h3>
                    <p className={`text-sm font-medium ${colors.text}`}>{profile.tagline}</p>
                  </div>
                </div>
                <div className="px-6 py-5 bg-white">
                  <p className="text-slate-600 leading-relaxed text-[15px]">{profile.bio}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-amber-600 via-orange-600 to-rose-700 text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">See yourself in these profiles?</h2>
          <p className="text-amber-100 text-lg mb-8">Apply now to join the inaugural pilot cohort launching June 2026.</p>
          <Button
            asChild
            size="lg"
            className="bg-white text-amber-700 hover:bg-amber-50 px-10 py-6 text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105 group"
            data-testid="ideal-candidate-apply-btn"
          >
            <a href="https://form.jotform.com/261314658584060" target="_blank" rel="noopener noreferrer">
              Apply to the Program
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IdealCandidate;
